import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { hierarchy, treemap, treemapSquarify } from 'd3';
import { useStore } from '../../../store/store';
import { observable, runInAction } from 'mobx';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../utils/logo';
import pubSub from '../../../utils/pubsub';
import ChipsList from '../../common/ChipsList';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import { HEATMAP_COLORS, HEATMAP_THRESHOLDS } from '../../../utils/constants/heatmapColors';
import { getResultsMobileBarInset } from '../../../utils/resultsMobileBar';
import {
  clampContextMenuPosition,
  getContextMenuHeight,
  CONTEXT_MENU_WIDTH
} from './contextMenuPosition';
import { useScrollZoomHint } from './scrollZoomHint';
import ScrollHintOverlay from './ScrollHintOverlay';

const ZOOM_LEVELS = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 15, 18, 20, 22, 25, 28, 30];

const findClosestZoomLevel = (targetScale) => {
  if (targetScale <= ZOOM_LEVELS[0]) return ZOOM_LEVELS[0];
  if (targetScale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1])
    return ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

  let closest = ZOOM_LEVELS[0];
  let minDiff = Math.abs(targetScale - closest);

  for (const level of ZOOM_LEVELS) {
    const diff = Math.abs(targetScale - level);
    if (diff < minDiff) {
      minDiff = diff;
      closest = level;
    }
  }

  return closest;
};

const getNextZoomLevel = (currentScale, zoomIn) => {
  const currentIndex = ZOOM_LEVELS.findIndex((level) => Math.abs(level - currentScale) < 0.01);

  if (currentIndex === -1) {
    return findClosestZoomLevel(currentScale);
  }

  if (zoomIn) {
    return currentIndex < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[currentIndex + 1] : currentScale;
  } else {
    return currentIndex > 0 ? ZOOM_LEVELS[currentIndex - 1] : currentScale;
  }
};

const OpenInNewTabContext = observer(
  ({ contextMenu, setContextMenu, openCompanyInNewTab, getIsExcludingResult, excludeResult }) => {
    const showExclusion = !!getIsExcludingResult && !!excludeResult;
    const isInExcludeAction = showExclusion ? getIsExcludingResult(contextMenu.data.id) : false;

    const constrainedPos = clampContextMenuPosition({
      x: contextMenu.x,
      y: contextMenu.y,
      menuWidth: CONTEXT_MENU_WIDTH,
      menuHeight: getContextMenuHeight(showExclusion),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      bottomInset: getResultsMobileBarInset()
    });

    return (
      <div
        style={{
          position: 'fixed',
          left: constrainedPos.x,
          top: constrainedPos.y,
          // Above the app header (20). This menu is `position: fixed` and clamps to y >= 10,
          // so it can open inside the header band; at 5 it only cleared the header by winning
          // on DOM order, which stopped being true when the header was raised.
          zIndex: 999
        }}
        onMouseEnter={() => {
          if (contextMenu.isFromTouch) {
            return;
          }

          savedState.setHoveredLeaf(contextMenu.leafId);
          savedState.setHoveredGroup(contextMenu.group);
        }}
        onMouseLeave={() => {
          if (contextMenu.isFromTouch) {
            return;
          }

          savedState.setHoveredLeaf(null);
          savedState.setHoveredGroup(null);
        }}
        onClick={(e) => e.stopPropagation()}
        className="icon-button-tooltip-context-link-menu"
      >
        <div
          className="context-link-menu-item"
          onClick={() => {
            openCompanyInNewTab(contextMenu.data);
            setContextMenu(null);
          }}
        >
          <div className="left-side">
            <div className="context-link-icon-wrap">
              <img src={ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT} />
            </div>
            <div className="context-link-text">
              Open in new tab
              <br />
              <div
                style={{
                  maxWidth: '112px',
                  fontSize: '8px',
                  lineHeight: '10px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {contextMenu.data.name}
              </div>
            </div>
          </div>
          <div className="right-side"></div>
        </div>
        {showExclusion && (
          <div
            className={`context-link-menu-item ${isInExcludeAction ? 'disabled' : ''}`}
            onClick={() => {
              if (isInExcludeAction) {
                return;
              }

              excludeResult(contextMenu.data, !contextMenu.data.isExcluded);
              setContextMenu(null);
            }}
          >
            <div className="left-side">
              <div className="context-link-icon-wrap">
                <img
                  src={
                    contextMenu.data.isExcluded
                      ? ICON_BUTTON_ICONS.ARROW_COUNTERCLOCKWISE
                      : ICON_BUTTON_ICONS.X_LG
                  }
                />
              </div>
              <div className="context-link-text">
                {contextMenu.data.isExcluded ? 'Restore result' : 'Exclude result'}
              </div>
            </div>
            <div className="right-side"></div>
          </div>
        )}
      </div>
    );
  }
);

const groupCompanyStats = (results, colorProperty, groupProperty) => {
  if (!groupProperty) return null;

  const grouped = new Map();

  for (const company of results) {
    const groupValue = company[groupProperty];
    if (!grouped.has(groupValue)) grouped.set(groupValue, []);
    grouped.get(groupValue).push(company);
  }

  const bestByGroup = new Map();
  const worstByGroup = new Map();

  for (const [group, companies] of grouped.entries()) {
    const filteredBest = companies.filter((c) => c[colorProperty] > HEATMAP_THRESHOLDS.HIGH);
    const filteredWorst = companies.filter((c) => c[colorProperty] < HEATMAP_THRESHOLDS.LOW);

    const sortedBest = filteredBest.sort((a, b) => b[colorProperty] - a[colorProperty]);
    const sortedWorst = filteredWorst.sort((a, b) => a[colorProperty] - b[colorProperty]);

    const bestCount = Math.ceil(sortedBest.length * 0.25);
    const worstCount = Math.ceil(sortedWorst.length * 0.25);

    bestByGroup.set(group, new Set(sortedBest.slice(0, bestCount).map((c) => c.id)));
    worstByGroup.set(group, new Set(sortedWorst.slice(0, worstCount).map((c) => c.id)));
  }

  return { bestByGroup, worstByGroup };
};

const getBest25PercentCompanies = (
  companiesSortedByColorProperty,
  colorProperty,
  groupedCompanyStats
) => {
  // if (groupedCompanyStats) return null; // for activation of group based coloring

  const bestCompanies = companiesSortedByColorProperty.filter(
    (company) => company[colorProperty] > HEATMAP_THRESHOLDS.HIGH
  );
  const count = Math.ceil(bestCompanies.length * 0.25);
  return new Set(bestCompanies.slice(0, count).map((company) => company.id));
};

const getWorst25PercentCompanies = (
  companiesSortedByColorProperty,
  colorProperty,
  groupedCompanyStats
) => {
  // if (groupedCompanyStats) return null; // for activation of group based coloring

  const worstCompanies = companiesSortedByColorProperty.filter(
    (company) => company[colorProperty] < HEATMAP_THRESHOLDS.LOW
  );
  const count = Math.ceil(worstCompanies.length * 0.25);
  return new Set(worstCompanies.slice(-count).map((company) => company.id));
};

const getCompaniesColorMap = (
  results,
  groupProperty,
  colorProperty,
  groupedCompanyStats,
  best25PercentCompanies,
  worst25PercentCompanies,
  companiesSortedByColorProperty
) => {
  const map = new Map();

  // const groupOrFlat = groupProperty ? results : companiesSortedByColorProperty; // for activation of group based coloring
  const groupOrFlat = results;

  const best = [],
    bestRest = [],
    worst = [],
    worstRest = [];
  for (const item of groupOrFlat) {
    const value = item[colorProperty];
    if (value > HEATMAP_THRESHOLDS.HIGH) {
      // for activation of group based coloring
      // const isBest = groupProperty
      //   ? groupedCompanyStats?.bestByGroup.get(item[groupProperty])?.has(item.id)
      //   : best25PercentCompanies?.has(item.id);
      const isBest = best25PercentCompanies?.has(item.id);
      (isBest ? best : bestRest).push(item);
    } else if (value < HEATMAP_THRESHOLDS.LOW) {
      // for activation of group based coloring
      // const isWorst = groupProperty
      //   ? groupedCompanyStats?.worstByGroup.get(item[groupProperty])?.has(item.id)
      //   : worst25PercentCompanies?.has(item.id);
      const isWorst = worst25PercentCompanies?.has(item.id);
      (isWorst ? worst : worstRest).push(item);
    }
  }

  // may be implemented to do some gradient coloring
  // const assignColors = (list, startColor, endColor) => {
  //   const values = list.map((item) => item[colorProperty]);
  //   const min = Math.min(...values);
  //   const max = Math.max(...values);
  //   // TBD
  // };

  const assignColors = (list = [], color = '') => {
    list.forEach((item) => {
      map.set(item.id, color);
    });
  };

  assignColors(best, HEATMAP_COLORS.BEST);
  assignColors(bestRest, HEATMAP_COLORS.BEST_REST);
  assignColors(worst, HEATMAP_COLORS.WORST);
  assignColors(worstRest, HEATMAP_COLORS.WORST_REST);

  for (const item of groupOrFlat) {
    if (!map.has(item.id)) {
      map.set(item.id, HEATMAP_COLORS.NEUTRAL);
    }
  }

  return map;
};

// One tile per company: a positive size as it is, any other at minSize so the company still shows.
const toTile = (item, sizeProperty, minSize, colors) => ({
  ...item,
  name: item.name || 'Unnamed',
  value: item[sizeProperty] > 0 ? item[sizeProperty] : minSize,
  color: colors.get(item.id) || HEATMAP_COLORS.NEUTRAL
});

// The tiles by their company's group value, 'Other' without one, in the order of the results.
const groupTiles = (results, groupProperty, tileOf) => {
  const groups = {};
  for (const item of results) {
    const groupKey = item[groupProperty] || 'Other';
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(tileOf(item));
  }
  return groups;
};

const groupNameOf = (groupName, groupProperty, utilsStore) =>
  groupProperty === '_industryCode'
    ? utilsStore.getIndustryCodesDisplayNames([groupName])[0]
    : groupName;

// One child per group, or only the selected group's tiles when it is among them.
const buildGroupedRoot = (groups, groupProperty, selectedGroup, utilsStore) => {
  const children = Object.entries(groups).map(([groupName, tiles]) => ({
    name: groupNameOf(groupName, groupProperty, utilsStore),
    groupProperty: groupName,
    children: tiles.map((tile) => ({ ...tile, children: undefined }))
  }));
  const group = selectedGroup && children.find((child) => child.groupProperty === selectedGroup);
  return group ? { name: 'singleGroup', children: group.children } : { name: 'root', children };
};

const normalizeResults = (
  results,
  utilsStore,
  colors,
  groupProperty,
  sizeProperty,
  selectedGroup
) => {
  const positiveCompanies = results.filter((item) => item[sizeProperty] > 0);
  const avgPositive =
    positiveCompanies.reduce((sum, item) => sum + item[sizeProperty], 0) / positiveCompanies.length;
  const minSize = avgPositive * 0.01;
  const tileOf = (item) => toTile(item, sizeProperty, minSize, colors);

  if (groupProperty) {
    return buildGroupedRoot(
      groupTiles(results, groupProperty, tileOf),
      groupProperty,
      selectedGroup,
      utilsStore
    );
  }
  return { name: 'flat', children: results.length ? results.map(tileOf) : undefined };
};

const buildRoot = (
  preparedResults,
  chartWidth,
  chartHeight,
  groupProperty,
  selectedGroup,
  searchCategory
) => {
  const isShowingGroups = groupProperty && !selectedGroup;

  const rootNode = hierarchy(preparedResults)
    .sum((d) => d.value || 0)
    .sort((a, b) => {
      const leafDepth = !isShowingGroups ? 1 : 2;
      const groupDepth = !isShowingGroups ? 0 : 1;
      const isCharacteristicsSearch =
        searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics;

      if (a.depth === groupDepth && b.depth === groupDepth) {
        if (isCharacteristicsSearch) {
          const groupsOrder = ['Very high', 'High', 'Medium', 'Low'];
          const aIndex = groupsOrder.indexOf(a.data.groupProperty);
          const bIndex = groupsOrder.indexOf(b.data.groupProperty);

          return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
        }

        return b.value - a.value;
      }

      if (a.depth === leafDepth && b.depth === leafDepth && a.parent === b.parent) {
        // if (isCharacteristicsSearch) {
        //   const scoringOrder = ['Very high', 'High', 'Medium', 'Low'];
        //   const aIndex = scoringOrder.indexOf(a.data.scoreLabel);
        //   const bIndex = scoringOrder.indexOf(b.data.scoreLabel);

        //   const aScore = aIndex === -1 ? Infinity : aIndex;
        //   const bScore = bIndex === -1 ? Infinity : bIndex;

        //   if (aScore === bScore) {
        //     return b.value - a.value;
        //   }

        //   return aScore - bScore;
        // }

        return b.value - a.value;
      }

      return 0;
    });

  const leafs = isShowingGroups
    ? rootNode.children.reduce((acc, child) => {
        return acc.concat(child.children || []);
      }, [])
    : rootNode.children.slice();

  if (isShowingGroups) {
    const groupNodes = rootNode.children || [];

    groupNodes.forEach((groupNode) => {
      groupNode.value = (groupNode.children || []).reduce((sum, child) => sum + child.value, 0);
    });

    const totalValue = groupNodes.reduce((sum, g) => sum + g.value, 0);
    const minThreshold = totalValue * 0.05;

    const smallGroups = [];
    const largeGroups = [];

    groupNodes.forEach((groupNode) => {
      if (groupNode.value < minThreshold) {
        smallGroups.push(groupNode);
      } else {
        largeGroups.push(groupNode);
      }
    });

    const adjustedTotalForSmall = smallGroups.length * minThreshold;
    const remainingTotal = totalValue - adjustedTotalForSmall;

    const largeGroupsOriginalTotal = largeGroups.reduce((sum, g) => sum + g.value, 0);

    smallGroups.forEach((g) => {
      g.newValue = minThreshold;
    });

    largeGroups.forEach((g) => {
      const proportion = g.value / largeGroupsOriginalTotal;
      g.newValue = remainingTotal * proportion;
    });

    groupNodes.forEach((groupNode) => {
      const newValue = groupNode.newValue;

      groupNode.value = newValue;

      const children = groupNode.children || [];
      const childrenTotal = children.reduce((sum, c) => sum + c.value, 0);

      if (childrenTotal > 0) {
        const scaleFactor = newValue / childrenTotal;
        children.forEach((child) => {
          child.value *= scaleFactor;
        });
      }
    });

    rootNode.value = groupNodes.reduce((sum, g) => sum + g.value, 0);
  }

  if (isShowingGroups) {
    rootNode.children?.forEach((groupNode) => {
      groupNode.value = (groupNode.children || []).reduce((sum, child) => sum + child.value, 0);
    });

    rootNode.value = (rootNode.children || []).reduce((sum, child) => sum + child.value, 0);
  } else {
    rootNode.value = leafs.reduce((sum, leaf) => sum + leaf.value, 0);
  }

  const treemapSliceHorizontal = (parent, x0, y0, x1, y1) => {
    const nodes = parent.children;
    const totalValue = parent.value;

    let currentX = x0;

    nodes.forEach((node) => {
      const width = (node.value / totalValue) * (x1 - x0);
      node.x0 = currentX;
      node.y0 = y0;
      node.x1 = currentX + width;
      node.y1 = y1;
      currentX += width;
    });
  };

  const treemapLayout = treemap()
    .size([chartWidth, isShowingGroups ? chartHeight - 24 : chartHeight])
    .paddingOuter((node) => (node.depth === 0 && isShowingGroups ? 3 : 0))
    .round(true);
  // .tile(treemapSquarify);

  if (isShowingGroups) {
    treemapLayout
      .paddingInner((node) => (node.depth === 0 ? 4 : 0))
      .tile((node, x0, y0, x1, y1) => {
        if (node.depth === 0) {
          // For the root level (groups), use horizontal slicing
          treemapSliceHorizontal(node, x0, y0, x1, y1);
          // Then apply squarify to children within each group
          node.children.forEach((child) => {
            if (child.children) {
              treemapSquarify(child, child.x0, child.y0, child.x1, child.y1);
            }
          });
        } else {
          // For deeper levels, use normal squarify
          treemapSquarify(node, x0, y0, x1, y1);
        }
      });
  } else {
    treemapLayout.paddingOuter(2).tile(treemapSquarify);
  }

  treemapLayout(rootNode);

  return rootNode.descendants();
};

const blendWithWhite = (color, ratio = 0.5) => {
  const hex = color.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const blendedR = Math.round(r * (1 - ratio) + 255 * ratio);
  const blendedG = Math.round(g * (1 - ratio) + 255 * ratio);
  const blendedB = Math.round(b * (1 - ratio) + 255 * ratio);

  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(blendedR)}${toHex(blendedG)}${toHex(blendedB)}`;
};

const savedState = observable({
  zoomStates: {},

  saveZoomState: (searchId, zoomState) => {
    if (savedState.isRestoring) {
      return;
    }

    runInAction(() => {
      // Calculate relative viewport center for dimension-independent restoration
      const viewportCenterX =
        (zoomState.containerWidth / 2 - zoomState.translateX) / zoomState.zoomLevel;
      const viewportCenterY =
        (zoomState.containerHeight / 2 - zoomState.translateY) / zoomState.zoomLevel;

      // Store as percentages of the content dimensions
      const relativeX =
        zoomState.containerWidth > 0 ? viewportCenterX / zoomState.containerWidth : 0.5;
      const relativeY =
        zoomState.containerHeight > 0 ? viewportCenterY / zoomState.containerHeight : 0.5;

      savedState.zoomStates[searchId] = {
        zoomLevel: zoomState.zoomLevel,
        translateX: zoomState.translateX,
        translateY: zoomState.translateY,
        containerWidth: zoomState.containerWidth,
        containerHeight: zoomState.containerHeight,
        // Enhanced: store relative position for better cross-dimension restoration
        relativeViewportCenterX: Math.max(0, Math.min(1, relativeX)),
        relativeViewportCenterY: Math.max(0, Math.min(1, relativeY)),
        timestamp: Date.now()
      };
    });
  },

  getZoomState: (searchId) => {
    return savedState.zoomStates[searchId];
  },

  restoreZoomState: (searchId, newWidth, newHeight, constrainZoomFn) => {
    const savedZoom = savedState.getZoomState(searchId);
    if (!savedZoom || !savedZoom.containerWidth || !savedZoom.containerHeight) {
      return null;
    }

    // Strategy 1: If dimensions are similar, use the old method
    const widthRatio = newWidth / savedZoom.containerWidth;
    const heightRatio = newHeight / savedZoom.containerHeight;
    const dimensionsAreSimilar = Math.abs(widthRatio - 1) < 0.1 && Math.abs(heightRatio - 1) < 0.1;

    if (dimensionsAreSimilar) {
      // Use simple scaling for similar dimensions
      const newTranslateX = savedZoom.translateX * widthRatio;
      const newTranslateY = savedZoom.translateY * heightRatio;

      const constrained = constrainZoomFn
        ? constrainZoomFn(newTranslateX, newTranslateY, savedZoom.zoomLevel)
        : { translateX: newTranslateX, translateY: newTranslateY };

      return {
        zoomLevel: savedZoom.zoomLevel,
        translateX: constrained.translateX,
        translateY: constrained.translateY,
        isDragging: false,
        isActuallyDragging: false,
        isPinching: false
      };
    }

    // Strategy 2: For different dimensions, use relative positioning
    if (
      savedZoom.relativeViewportCenterX !== undefined &&
      savedZoom.relativeViewportCenterY !== undefined
    ) {
      // Calculate where the viewport center should be in the new dimensions
      const targetViewportCenterX = savedZoom.relativeViewportCenterX * newWidth;
      const targetViewportCenterY = savedZoom.relativeViewportCenterY * newHeight;

      // Calculate translation to center the viewport at the target position
      const newTranslateX = newWidth / 2 - targetViewportCenterX * savedZoom.zoomLevel;
      const newTranslateY = newHeight / 2 - targetViewportCenterY * savedZoom.zoomLevel;

      const constrained = constrainZoomFn
        ? constrainZoomFn(newTranslateX, newTranslateY, savedZoom.zoomLevel)
        : { translateX: newTranslateX, translateY: newTranslateY };

      return {
        zoomLevel: savedZoom.zoomLevel,
        translateX: constrained.translateX,
        translateY: constrained.translateY,
        isDragging: false,
        isActuallyDragging: false,
        isPinching: false
      };
    }

    // Strategy 3: Fallback - maintain zoom but center the view
    const constrained = constrainZoomFn
      ? constrainZoomFn(0, 0, savedZoom.zoomLevel)
      : { translateX: 0, translateY: 0 };

    return {
      zoomLevel: savedZoom.zoomLevel,
      translateX: constrained.translateX,
      translateY: constrained.translateY,
      isDragging: false,
      isActuallyDragging: false,
      isPinching: false
    };
  },

  isRestoring: true,
  setIsRestoring: (value) => {
    runInAction(() => {
      savedState.isRestoring = value;
    });
  },

  selectedSingleGroup: {},
  setSelectedSingleGroup: (group, searchId) => {
    runInAction(() => {
      savedState.selectedSingleGroup[searchId] = group;
    });
  },

  hoveredGroup: null,
  setHoveredGroup: (group) => {
    runInAction(() => {
      savedState.hoveredGroup = group;
    });
  },

  hoveredLeaf: null,
  showHoveredLeaf: false,
  setHoveredLeaf: (id) => {
    runInAction(() => {
      savedState.hoveredLeaf = id;
      savedState.showHoveredLeaf = false;
    });

    setTimeout(() => {
      if (savedState.hoveredLeaf === id) {
        runInAction(() => {
          savedState.showHoveredLeaf = true;
        });
      }
    }, 300);
  }
});

pubSub.subscribe((payload) => {
  if (
    payload?.event === 'resetProfilerHeatmapSavedState' &&
    payload?.searchId &&
    savedState.getZoomState(payload.searchId)
  ) {
    const { containerWidth, containerHeight } = savedState.getZoomState(payload.searchId);
    savedState.saveZoomState(payload.searchId, {
      zoomLevel: 1,
      translateX: 0,
      translateY: 0,
      containerWidth,
      containerHeight
    });
  }
});

/** How long a finger must rest on a tile before the context menu opens. */
const TILE_LONG_PRESS_MS = 500;

/**
 * Movement that cancels a pending long press, in px. Matches the drag threshold the
 * pan/zoom handlers use (`distanceFromStart > 5`): anything the component already
 * reads as a drag must not also arrive as a long press.
 */
const TILE_LONG_PRESS_MOVE_TOLERANCE = 5;

/**
 * Movement along the page-scroll axis that cancels a pending long press where the page
 * owns one-finger scrolling, in px. Much tighter than the tolerance above, because iOS
 * stops delivering `touchmove` the moment it claims the gesture for a pan (and often
 * skips `touchcancel`), so a slow scroll would never reach the full 5px. Not zero: a
 * resting thumb still emits `touchmove` as the contact patch shifts, and cancelling on
 * any movement at all would make the long press unreachable on the stacked heatmap.
 */
const TILE_LONG_PRESS_SCROLL_TOLERANCE = 2;

/**
 * How long after a touch we keep treating mouse events as synthetic. iOS Safari
 * replays `mouseover`/`mouseenter`/`contextmenu` on the tapped element after a
 * touch, and Android Chrome fires `contextmenu` alongside our own long-press timer;
 * both must be ignored, or a tap raises the hover card and a second `setContextMenu`
 * write clobbers `isFromTouch`. A capability check can't do this job —
 * `isPhoneOrTablet` is UA-based (false on iPadOS, false in jsdom) and a hybrid
 * laptop is genuinely both.
 */
const TOUCH_RECENCY_MS = 700;

/**
 * Cancel a tile's pending long press and forget the gesture's bookkeeping. The state
 * lives on the DOM node (not React state) so it survives across the separate touch
 * handlers without re-rendering 1000+ tiles; every exit path must come through here,
 * or the timer outlives the gesture and fires after the finger is gone.
 */
const clearTilePress = (tile) => {
  if (!tile) return;
  if (tile.longPressTimeout) {
    clearTimeout(tile.longPressTimeout);
    tile.longPressTimeout = null;
  }
  tile.startPoint = null;
  tile.totalMovement = 0;
};

const ProfilerHeatmap = observer(
  ({
    results = [],
    displayDetails,
    searchId,
    sizeProperty,
    colorProperty,
    groupProperty,
    setToggleProfilerHeatmapGroupingCallback,
    showCagr4Y = false,
    showEbitMargin = true,
    project = null,
    searchCategory = null,
    getIsExcludingResult,
    excludeResult,
    // TD-2720 / TD-2733 — set by <ProfilerHeatmapSection/>, i.e. by every surface that
    // stacks a company list below the tiles: search results, saved projects and team
    // feedback. There, a plain wheel or a one-finger swipe has to scroll the page to
    // reach the list; zoom and pan move to Ctrl/Cmd+wheel and two-finger gestures, the
    // contract embedded maps use. The section hard-codes it rather than exposing it as a
    // prop, because "there is a list underneath" is what defines that section.
    //
    // Comparables (ComparableCompaniesHeatmapView) is the one remaining call site that
    // owns its whole viewport — and it renders inside the company-details popup, where
    // `touchAction: pan-y` would have nothing to scroll — so it keeps wheel-to-zoom and
    // one-finger pan.
    allowPageScroll = false
  }) => {
    const { utilsStore } = useStore();
    const state = useLocalObservable(() => ({
      dimensions: { width: 0, height: 0 },
      setDimensions: (width, height) => {
        [state.dimensions.width, state.dimensions.height] = [width, height];
      },
      contextMenu: null,
      setContextMenu: (context) => {
        state.contextMenu = context;
      },
      zoom: {
        zoomLevel: 1,
        translateX: 0,
        translateY: 0,
        isDragging: false,
        isActuallyDragging: false,
        dragStartPoint: { x: 0, y: 0 },
        lastPanPoint: { x: 0, y: 0 },
        initialDistance: 0,
        initialZoomLevel: 1,
        initialCenter: { x: 0, y: 0 },
        initialTranslateX: 0,
        initialTranslateY: 0,
        isPinching: false
      },
      interaction: {
        startTime: 0,
        startPoint: { x: 0, y: 0 },
        totalMovement: 0,
        isGesture: false,
        gestureType: null
      },
      resetInteraction: () => {
        state.interaction.startTime = 0;
        state.interaction.startPoint = { x: 0, y: 0 };
        state.interaction.totalMovement = 0;
        state.interaction.isGesture = false;
        state.interaction.gestureType = null;
      },
      updateInteraction: (updates) => {
        Object.assign(state.interaction, updates);
      },
      setZoom: (updates) => {
        Object.assign(state.zoom, updates);
      },
      resetZoom: () => {
        state.setZoom({
          zoomLevel: 1,
          translateX: 0,
          translateY: 0,
          isDragging: false,
          isActuallyDragging: false,
          isPinching: false
        });

        savedState.saveZoomState(searchId, {
          zoomLevel: 1,
          translateX: 0,
          translateY: 0,
          containerWidth: state.dimensions.width,
          containerHeight: state.dimensions.height
        });
      }
    }));

    const heatmapRef = useRef(null);
    // TD-2750 — which hint to show over the tiles, or null (scrollZoomHint.js).
    const [scrollHint, showScrollHint, clearScrollHint] = useScrollZoomHint();
    const innerWrapRef = useRef(null);
    // TD-2772 — timestamp of the last touch that started on a tile. Read by the
    // mouse-driven handlers to ignore the events iOS/Android synthesize after a tap.
    const lastTouchAtRef = useRef(0);
    const isTouchRecent = () => Date.now() - lastTouchAtRef.current < TOUCH_RECENCY_MS;

    useEffect(() => {
      const onViewportResize = () => {
        if (state.contextMenu) {
          pubSub.publish({ event: 'icon-button-context-menu-trigger' });
        }
      };
      window.addEventListener('resize', onViewportResize);
      return () => {
        window.removeEventListener('resize', onViewportResize);
      };
    }, [state]);

    const openCompanyInNewTab = (company) => {
      utilsStore.openCompanyDetails(company.id, { ref: 'search results' });
    };

    const constrainZoom = (translateX, translateY, zoomLevel) => {
      if (zoomLevel <= 1) {
        return {
          translateX: 0,
          translateY: 0
        };
      }

      const scaledWidth = state.dimensions.width * zoomLevel;
      const scaledHeight = state.dimensions.height * zoomLevel;

      const maxTranslateX = scaledWidth - state.dimensions.width + state.dimensions.width * 0.5;
      const maxTranslateY = scaledHeight - state.dimensions.height + state.dimensions.height * 0.5;

      const constrainedTranslateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX));
      const constrainedTranslateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, translateY));

      return {
        translateX: constrainedTranslateX,
        translateY: constrainedTranslateY
      };
    };

    const getEventPoint = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };

    const getTouchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getTouchCenter = (touches) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    const shouldAllowClick = useCallback(() => {
      if (state.interaction.startTime === 0) {
        return true;
      }

      const timeSinceStart = Date.now() - state.interaction.startTime;
      const wasQuickTap = timeSinceStart < 500;
      const hadMinimalMovement = state.interaction.totalMovement < 15;
      const wasNotGesture = !state.interaction.isGesture;

      return wasQuickTap && hadMinimalMovement && wasNotGesture;
    }, []);

    const handleElementTouchEnd = useCallback((e, action) => {
      e.preventDefault();
      e.stopPropagation();

      const timeSinceStart = Date.now() - state.interaction.startTime;
      const wasQuickTap = timeSinceStart < 500;
      const hadMinimalMovement = state.interaction.totalMovement < 15;
      const wasNotGesture = !state.interaction.isGesture;

      if (wasQuickTap && hadMinimalMovement && wasNotGesture) {
        state.resetInteraction();
        action();
      } else {
        state.resetInteraction();
      }
    }, []);

    const handleWheel = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const currentZoomLevel = state.zoom.zoomLevel;
        const zoomIn = e.deltaY < 0;

        const newZoomLevel = getNextZoomLevel(currentZoomLevel, zoomIn);

        if (newZoomLevel === currentZoomLevel) {
          return;
        }

        const rect = heatmapRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const zoomRatio = newZoomLevel / currentZoomLevel;
        const newTranslateX = mouseX - zoomRatio * (mouseX - state.zoom.translateX);
        const newTranslateY = mouseY - zoomRatio * (mouseY - state.zoom.translateY);

        const constrainedTranslation = constrainZoom(newTranslateX, newTranslateY, newZoomLevel);

        state.setZoom({
          ...state.zoom,
          zoomLevel: newZoomLevel,
          ...constrainedTranslation
        });

        if (!savedState.isRestoring) {
          savedState.saveZoomState(searchId, {
            zoomLevel: newZoomLevel,
            ...constrainedTranslation,
            containerWidth: state.dimensions.width,
            containerHeight: state.dimensions.height
          });
        }

        if (state.contextMenu) {
          pubSub.publish({ event: 'icon-button-context-menu-trigger' });
        }
      },
      [searchId]
    );

    const handleMouseDown = useCallback((e) => {
      if (e.button !== 0) return;

      const point = getEventPoint(e);

      state.resetInteraction();
      state.updateInteraction({
        startTime: Date.now(),
        startPoint: point,
        gestureType: 'potential-drag'
      });

      state.setZoom({
        isDragging: true,
        isActuallyDragging: false,
        dragStartPoint: point,
        lastPanPoint: point
      });

      e.preventDefault();
    }, []);

    const handleMouseMove = useCallback(
      (e) => {
        if (!state.zoom.isDragging) return;

        const point = getEventPoint(e);
        const deltaX = point.x - state.zoom.lastPanPoint.x;
        const deltaY = point.y - state.zoom.lastPanPoint.y;

        const movementDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        state.updateInteraction({
          totalMovement: state.interaction.totalMovement + movementDistance
        });

        const distanceFromStart = Math.sqrt(
          Math.pow(point.x - state.zoom.dragStartPoint.x, 2) +
            Math.pow(point.y - state.zoom.dragStartPoint.y, 2)
        );

        if (distanceFromStart > 5 && !state.zoom.isActuallyDragging) {
          state.setZoom({ isActuallyDragging: true });
          state.updateInteraction({
            isGesture: true,
            gestureType: 'drag'
          });
          if (state.contextMenu) {
            pubSub.publish({ event: 'icon-button-context-menu-trigger' });
          }
        }

        if (state.zoom.isActuallyDragging) {
          const constrainedTranslation = constrainZoom(
            state.zoom.translateX + deltaX,
            state.zoom.translateY + deltaY,
            state.zoom.zoomLevel
          );
          state.setZoom({
            ...state.zoom,
            ...constrainedTranslation
          });
        }

        state.setZoom({ lastPanPoint: point });
        e.preventDefault();
      },
      [state.zoom]
    );

    const handleMouseUp = useCallback(() => {
      const shouldSave = state.zoom.isActuallyDragging;

      state.setZoom({
        isDragging: false,
        isActuallyDragging: false
      });

      if (shouldSave && !savedState.isRestoring) {
        savedState.saveZoomState(searchId, {
          zoomLevel: state.zoom.zoomLevel,
          translateX: state.zoom.translateX,
          translateY: state.zoom.translateY,
          containerWidth: state.dimensions.width,
          containerHeight: state.dimensions.height
        });
      }
    }, [searchId]);

    const handleTouchStart = useCallback(
      (e) => {
        if (e.touches.length === 1) {
          const point = getEventPoint(e);

          state.resetInteraction();
          state.updateInteraction({
            startTime: Date.now(),
            startPoint: point,
            gestureType: 'potential-drag'
          });

          state.setZoom({
            isDragging: true,
            isActuallyDragging: false,
            isPinching: false,
            dragStartPoint: point,
            lastPanPoint: point
          });
        } else if (e.touches.length === 2) {
          const distance = getTouchDistance(e.touches);
          const center = getTouchCenter(e.touches);
          const rect = heatmapRef.current?.getBoundingClientRect();
          if (!rect) return;

          const relativeCenter = {
            x: center.x - rect.left,
            y: center.y - rect.top
          };

          state.resetInteraction();
          state.updateInteraction({
            startTime: Date.now(),
            startPoint: center,
            gestureType: 'pinch',
            isGesture: true
          });

          state.setZoom({
            isDragging: false,
            isActuallyDragging: false,
            isPinching: true,
            initialDistance: distance,
            initialZoomLevel: state.zoom.zoomLevel,
            initialCenter: relativeCenter,
            initialTranslateX: state.zoom.translateX,
            initialTranslateY: state.zoom.translateY
          });

          if (state.contextMenu) {
            pubSub.publish({ event: 'icon-button-context-menu-trigger' });
          }
        }

        e.preventDefault();
      },
      [state.zoom]
    );

    const handleTouchMove = useCallback(
      (e) => {
        if (e.touches.length === 1 && state.zoom.isDragging && !state.zoom.isPinching) {
          const point = getEventPoint(e);
          const deltaX = point.x - state.zoom.lastPanPoint.x;
          const deltaY = point.y - state.zoom.lastPanPoint.y;

          const movementDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
          state.updateInteraction({
            totalMovement: state.interaction.totalMovement + movementDistance
          });

          const distanceFromStart = Math.sqrt(
            Math.pow(point.x - state.zoom.dragStartPoint.x, 2) +
              Math.pow(point.y - state.zoom.dragStartPoint.y, 2)
          );

          if (distanceFromStart > 5 && !state.zoom.isActuallyDragging) {
            state.setZoom({ isActuallyDragging: true });
            state.updateInteraction({
              isGesture: true,
              gestureType: 'drag'
            });
            if (state.contextMenu) {
              pubSub.publish({ event: 'icon-button-context-menu-trigger' });
            }
          }

          if (state.zoom.isActuallyDragging) {
            const constrainedTranslation = constrainZoom(
              state.zoom.translateX + deltaX,
              state.zoom.translateY + deltaY,
              state.zoom.zoomLevel
            );
            state.setZoom({
              ...state.zoom,
              ...constrainedTranslation
            });
          }

          state.setZoom({ lastPanPoint: point });
        } else if (
          e.touches.length === 2 &&
          state.zoom.isPinching &&
          state.zoom.initialDistance > 0
        ) {
          const distance = getTouchDistance(e.touches);
          const center = getTouchCenter(e.touches);
          const rect = heatmapRef.current?.getBoundingClientRect();
          if (!rect) return;

          const currentCenter = {
            x: center.x - rect.left,
            y: center.y - rect.top
          };

          const scaleBy = distance / state.zoom.initialDistance;
          let newZoomLevel = state.zoom.initialZoomLevel * scaleBy;

          const minZoomLevel = ZOOM_LEVELS[0];
          const maxZoomLevel = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
          newZoomLevel = Math.max(minZoomLevel, Math.min(maxZoomLevel, newZoomLevel));

          const zoomPointX =
            (state.zoom.initialCenter.x - state.zoom.initialTranslateX) /
            state.zoom.initialZoomLevel;
          const zoomPointY =
            (state.zoom.initialCenter.y - state.zoom.initialTranslateY) /
            state.zoom.initialZoomLevel;

          const newTranslateX = currentCenter.x - zoomPointX * newZoomLevel;
          const newTranslateY = currentCenter.y - zoomPointY * newZoomLevel;

          const constrainedTranslation = constrainZoom(newTranslateX, newTranslateY, newZoomLevel);
          state.setZoom({
            ...state.zoom,
            zoomLevel: newZoomLevel,
            ...constrainedTranslation
          });
        }

        e.preventDefault();
      },
      [state.zoom]
    );

    const handleTouchEnd = useCallback(
      (e) => {
        const shouldSave = state.zoom.isPinching || state.zoom.isActuallyDragging;

        if (shouldSave) {
          savedState.saveZoomState(searchId, {
            zoomLevel: state.zoom.zoomLevel,
            translateX: state.zoom.translateX,
            translateY: state.zoom.translateY,
            containerWidth: state.dimensions.width,
            containerHeight: state.dimensions.height
          });
        }

        state.setZoom({
          isDragging: false,
          isActuallyDragging: false,
          isPinching: false,
          initialDistance: 0,
          initialZoomLevel: 1
        });

        if (!state.interaction.isGesture && state.interaction.totalMovement < 5) {
          setTimeout(() => {
            state.resetInteraction();
          }, 50);
        }
      },
      [searchId]
    );

    useEffect(() => {
      return () => {
        savedState.setHoveredGroup(null);
        savedState.setHoveredLeaf(null);
        runInAction(() => {
          savedState.showHoveredLeaf = false;
        });
      };
    }, [searchId]);

    useEffect(() => {
      state.resetZoom();
    }, [savedState.selectedSingleGroup[searchId]]);

    useEffect(() => {
      const onAnyClick = () => {
        state.setContextMenu(null);
      };
      const onPubSubContextClick = (payload) => {
        if (payload?.event === 'icon-button-context-menu-trigger') {
          state.setContextMenu(null);
        }
      };
      // TD-2772 — `click` alone can't dismiss on touch: handleElementTouchEnd calls
      // preventDefault(), which suppresses the synthetic click, so tapping another tile
      // left the menu open. Only dismiss an already-open menu, so the touch that is
      // about to open one doesn't close it in the same gesture (the opening path
      // publishes on the pubSub channel above, which still handles replacing it).
      // The menu itself only stops `click` propagation, so a tap on one of its rows
      // would otherwise unmount it here before the row's own click could run.
      const onAnyTouchStart = (event) => {
        if (!state.contextMenu) return;
        if (event.target?.closest?.('.icon-button-tooltip-context-link-menu')) return;
        state.setContextMenu(null);
      };
      pubSub.subscribe(onPubSubContextClick);
      document.addEventListener('click', onAnyClick);
      // Capture, not bubble: a tile's own `onTouchStart` calls `stopPropagation()`, and
      // React attaches its listeners at the root container — so in the bubble phase the
      // event never reaches `document` and a tap on another tile left the menu open.
      // Capture runs before the tile handler, which is safe because of the guard above:
      // the gesture that is about to *open* a menu sees `state.contextMenu` empty.
      document.addEventListener('touchstart', onAnyTouchStart, {
        passive: true,
        capture: true
      });

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        pubSub.unsubscribe(onPubSubContextClick);
        document.removeEventListener('click', onAnyClick);
        // `capture` participates in listener identity, so it has to be repeated here.
        document.removeEventListener('touchstart', onAnyTouchStart, { capture: true });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [state, handleMouseMove, handleMouseUp]);

    useEffect(() => {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          state.setDimensions(width, height);
        }
      });
      if (heatmapRef.current) {
        resizeObserver.observe(heatmapRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    }, [state]);

    useEffect(() => {
      setToggleProfilerHeatmapGroupingCallback((newValue) => {
        if (!newValue) {
          savedState.setSelectedSingleGroup(null, searchId);
        }
        setTimeout(() => {
          if (state.zoom.zoomLevel !== 1) {
            state.resetZoom();
          }
        }, 0);
      });
      return () => {
        setToggleProfilerHeatmapGroupingCallback();
      };
    }, [state, setToggleProfilerHeatmapGroupingCallback, searchId]);

    // TD-2720 — does this gesture belong to the page rather than the treemap?
    const belongsToPageScroll = useCallback(
      (event) => {
        if (!allowPageScroll) return false;
        if (event.type === 'wheel') return !event.ctrlKey && !event.metaKey;

        return event.touches?.length === 1 && !state.zoom.isPinching;
      },
      [allowPageScroll, state.zoom]
    );

    useEffect(() => {
      if (!heatmapRef.current) return;

      const element = heatmapRef.current;

      // TD-2720 — the gesture handlers below claim every wheel and touch event for
      // zoom and pan, which is right while the heatmap owns the viewport. Where it
      // shares the page with a list underneath (`allowPageScroll`), scrolling has to
      // reach the page instead, so those gestures are filtered out here rather than
      // inside the handlers: a plain wheel and one-finger swipes pass through
      // untouched, while Ctrl/Cmd+wheel and two-finger pinch still drive the
      // treemap. Wrapping at the boundary leaves the pan/zoom logic untouched.
      // TD-2750 — the pass-through point is also where the hint belongs: it is the moment we
      // know the analyst tried to interact with the tiles and the page moved instead. Keyed on
      // the event, not on screen size, so a touch laptop is told about fingers and a narrow
      // desktop window is told about the modifier.
      const passToPage = (handler) => (event) => {
        if (belongsToPageScroll(event)) {
          // `belongsToPageScroll` answers "should the page get this?", and a one-finger
          // touchstart is a legitimate yes. The hint asks something narrower — did someone
          // try to work the tiles and get the page instead — and contact alone is not that:
          // a tap opening a company would otherwise cover the map on every tap. Only
          // movement counts, so touchstart routes through without saying anything.
          if (event.type !== 'touchstart') {
            showScrollHint(event.type === 'wheel' ? 'wheel' : 'touch');
          }

          return;
        }

        handler(event);
      };

      const onWheel = passToPage(handleWheel);
      const onTouchStart = passToPage(handleTouchStart);
      const onTouchMove = passToPage(handleTouchMove);

      element.addEventListener('wheel', onWheel, { passive: false, capture: true });
      element.addEventListener('mousedown', handleMouseDown);
      element.addEventListener('touchstart', onTouchStart, { passive: false });
      element.addEventListener('touchmove', onTouchMove, { passive: false });
      element.addEventListener('touchend', handleTouchEnd);

      return () => {
        // The hint goes with the listeners that showed it: the re-run starts without one.
        clearScrollHint();
        element.removeEventListener('wheel', onWheel, { capture: true });
        element.removeEventListener('mousedown', handleMouseDown);
        element.removeEventListener('touchstart', onTouchStart);
        element.removeEventListener('touchmove', onTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
      };
    }, [
      belongsToPageScroll,
      showScrollHint,
      clearScrollHint,
      handleWheel,
      handleMouseDown,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd
    ]);

    const companiesSortedByColorProperty = useMemo(() => {
      return results.slice().sort((a, b) => b[colorProperty] - a[colorProperty]);
    }, [results, colorProperty]);

    const groupedCompanyStats = useMemo(() => {
      return groupCompanyStats(results, colorProperty, groupProperty);
    }, [results, colorProperty, groupProperty]);

    const [best25PercentCompanies, worst25PercentCompanies] = useMemo(() => {
      return [
        getBest25PercentCompanies(
          companiesSortedByColorProperty,
          colorProperty,
          groupedCompanyStats
        ),
        getWorst25PercentCompanies(
          companiesSortedByColorProperty,
          colorProperty,
          groupedCompanyStats
        )
      ];
    }, [companiesSortedByColorProperty, colorProperty, groupedCompanyStats]);

    const colors = useMemo(() => {
      return getCompaniesColorMap(
        results,
        groupProperty,
        colorProperty,
        groupedCompanyStats,
        best25PercentCompanies,
        worst25PercentCompanies,
        companiesSortedByColorProperty
      );
    }, [
      results,
      groupProperty,
      colorProperty,
      groupedCompanyStats,
      best25PercentCompanies,
      worst25PercentCompanies,
      companiesSortedByColorProperty
    ]);

    const preparedResults = useMemo(() => {
      return normalizeResults(
        results,
        utilsStore,
        colors,
        groupProperty,
        sizeProperty,
        savedState.selectedSingleGroup[searchId]
      );
    }, [
      results,
      utilsStore,
      colors,
      groupProperty,
      searchId,
      sizeProperty,
      savedState.selectedSingleGroup[searchId]
    ]);

    const root = useMemo(() => {
      return buildRoot(
        preparedResults,
        state.dimensions.width,
        state.dimensions.height,
        groupProperty,
        savedState.selectedSingleGroup[searchId],
        searchCategory
      );
    }, [
      preparedResults,
      state.dimensions.width,
      state.dimensions.height,
      groupProperty,
      searchId,
      savedState.selectedSingleGroup[searchId],
      searchCategory
    ]);

    useEffect(() => {
      return () => {
        savedState.setIsRestoring(true);
      };
    }, []);

    useEffect(() => {
      if (state.dimensions.width === 0 || state.dimensions.height === 0) {
        return;
      }

      const restoredZoomState = savedState.restoreZoomState(
        searchId,
        state.dimensions.width,
        state.dimensions.height,
        constrainZoom
      );

      if (restoredZoomState) {
        state.setZoom(restoredZoomState);
      }

      savedState.setIsRestoring(false);
    }, [searchId, state.dimensions.width, state.dimensions.height]);

    const scaledElements = useMemo(() => {
      const groups = [];
      const leaves = [];

      root.forEach((node) => {
        const { depth, data } = node;
        const hasChildren = data.children && data.children.length > 0;

        if (depth === 1 && hasChildren) {
          groups.push(node);
        } else if (depth > 1 || !hasChildren) {
          leaves.push(node);
        }
      });

      const scaleCoordinate = (coord) => coord * state.zoom.zoomLevel;
      const isShowingGroups = groupProperty && !savedState.selectedSingleGroup[searchId];

      return {
        groups: groups.map((node) => ({
          ...node,
          x0: scaleCoordinate(node.x0),
          y0: scaleCoordinate(node.y0),
          x1: scaleCoordinate(node.x1),
          y1: scaleCoordinate(node.y1) + (isShowingGroups ? 24 : 0)
        })),
        leaves: leaves.map((node) => ({
          ...node,
          x0: scaleCoordinate(node.x0),
          y0: scaleCoordinate(node.y0) + (isShowingGroups ? 24 : 0),
          x1: scaleCoordinate(node.x1),
          y1: scaleCoordinate(node.y1) + (isShowingGroups ? 24 : 0)
        }))
      };
    }, [root, state.zoom.zoomLevel, groupProperty, savedState.selectedSingleGroup[searchId]]);

    const showExclusion = !!getIsExcludingResult && !!excludeResult;

    const getTooltipConfig = (y0, y1) => {
      const leafCenterY = (y0 + y1) / 2 + state.zoom.translateY;
      const chartCenterY = state.dimensions.height / 2;
      const isInTopHalf = leafCenterY > chartCenterY;

      return isInTopHalf
        ? { position: 'top-center', style: { top: '27px' } }
        : { position: 'bottom-center', style: { bottom: '16px' } };
    };

    const transformStyle = {
      transform: `translate(${state.zoom.translateX}px, ${state.zoom.translateY}px)`,
      transformOrigin: '0 0',
      transition: 'none'
    };

    return (
      <div className="heatmap-wrap">
        {!!groupProperty && !!savedState.selectedSingleGroup[searchId] && (
          <div className="heatmap-selected-group-control-wrap">
            <button
              type="button"
              onClick={() => {
                savedState.setSelectedSingleGroup(null, searchId);
              }}
              className="pm-backlink"
            >
              Back
            </button>
            <div className="group-name">
              {searchCategory ===
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Industries && (
                <>
                  {
                    utilsStore.getIndustryCodesDisplayNames([
                      savedState.selectedSingleGroup[searchId]
                    ])[0]
                  }
                </>
              )}
              {searchCategory !==
                UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Industries && (
                <>{savedState.selectedSingleGroup[searchId]?.toUpperCase?.()}</>
              )}
            </div>
          </div>
        )}
        <div
          ref={heatmapRef}
          className="heatmap-inner-wrap"
          style={{
            // `clip` hides zoomed tiles without creating a scrollport. `hidden` is a
            // scroll container: Chromium (macOS trackpad) targets it, shows overlay
            // bars, and swallows the wheel so the page never moves — and `overscroll-
            // behavior: auto` on that box then scrolls the tiles first. Same pattern as
            // `.filter-checkbox-label`. `minHeight: 0` is required because `clip`, unlike
            // `hidden`, does not zero a flex item's automatic minimum, so this `flex: 1`
            // child would otherwise inflate over the list and the hint scrim with it.
            overflow: 'clip',
            minHeight: 0,
            cursor: state.zoom.isDragging ? 'grabbing' : 'grab',
            touchAction: allowPageScroll ? 'pan-y' : 'none',
            position: 'relative'
          }}
        >
          <ScrollHintOverlay hint={scrollHint} />
          <div ref={innerWrapRef} style={transformStyle}>
            {/* Render groups using scaled coordinates */}
            {groupProperty &&
              !savedState.selectedSingleGroup[searchId] &&
              scaledElements.groups.map((node, i) => {
                const { x0, y0, x1, y1, data } = node;
                const [w, h] = [x1 - x0, y1 - y0];
                const isHovered = savedState.hoveredGroup === data.groupProperty;

                return (
                  <div
                    key={`group-${i}-${data.groupProperty}`}
                    className={`group ${isHovered ? 'hovered' : ''}`}
                    style={{ left: x0, top: y0, width: w, height: h }}
                    onClick={(e) => {
                      if (!shouldAllowClick()) {
                        state.resetInteraction();
                        return;
                      }
                      state.resetInteraction();
                      savedState.setSelectedSingleGroup(data.groupProperty, searchId);
                    }}
                    onTouchEnd={(e) =>
                      handleElementTouchEnd(e, () =>
                        savedState.setSelectedSingleGroup(data.groupProperty, searchId)
                      )
                    }
                    onMouseEnter={() => savedState.setHoveredGroup(data.groupProperty)}
                    onMouseLeave={() => savedState.setHoveredGroup(null)}
                  ></div>
                );
              })}

            {/* Render group labels using scaled coordinates */}
            {groupProperty &&
              !savedState.selectedSingleGroup[searchId] &&
              scaledElements.groups.map((node, i) => {
                const { x0, y0, x1, data } = node;
                const w = x1 - x0;
                const isHovered = savedState.hoveredGroup === data.groupProperty;
                const groupName =
                  searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Industries
                    ? utilsStore.getIndustryCodesDisplayNames([data.groupProperty])[0]
                    : data.groupProperty?.toUpperCase?.();

                return (
                  <div
                    title={groupName}
                    key={`group-label-${i}-${data.groupProperty}`}
                    className={`group-label ${isHovered ? 'hovered' : ''}`}
                    style={{
                      left: x0,
                      top: y0,
                      width: w
                    }}
                    onMouseEnter={() => savedState.setHoveredGroup(data.groupProperty)}
                    onMouseLeave={() => savedState.setHoveredGroup(null)}
                    onClick={(e) => {
                      if (!shouldAllowClick()) {
                        state.resetInteraction();
                        return;
                      }
                      state.resetInteraction();
                      savedState.setSelectedSingleGroup(data.groupProperty, searchId);
                    }}
                    onTouchEnd={(e) =>
                      handleElementTouchEnd(e, () =>
                        savedState.setSelectedSingleGroup(data.groupProperty, searchId)
                      )
                    }
                  >
                    <div className="label">{groupName}</div>
                    <div className="label-symbol">〉</div>
                  </div>
                );
              })}

            {/* Render leaves using scaled coordinates but original leaf logic */}
            {scaledElements.leaves.map((node, i) => {
              const { x0, y0, x1, y1, data } = node;
              const [w, h] = [x1 - x0, y1 - y0];
              const isHovered = savedState.hoveredLeaf === data.id;
              const uniqueId = `leaf-${i}-${data.id}`;
              const showLogo = w >= 18 && h >= 18 && data.website_SAFE;

              const getLogoSizeForCheck = () => {
                if (!showLogo) return 0;
                const availableSpace = Math.min(w - 10, h - 10);
                const [maxSize, minSize] = [64, 10];
                return Math.max(minSize, Math.min(maxSize, availableSpace));
              };

              const logoSizeForCheck = getLogoSizeForCheck();
              const gap = showLogo ? 4 : 0;
              const padding = 8;
              const minTextHeight = 16;
              const availableTextSpace = showLogo
                ? h - logoSizeForCheck - gap - padding
                : h - padding;
              const showText = w >= 50 && availableTextSpace >= minTextHeight;
              const showBoth = showLogo && showText;

              const getLogoSize = () => {
                if (!showLogo) return 0;
                const availableSpace = showBoth ? Math.min(w - 10, h - 10) : Math.min(w - 8, h - 8);
                const [maxSize, minSize] = [64, 10];
                return Math.max(minSize, Math.min(maxSize, availableSpace));
              };

              const getTextStyles = () => {
                if (!showText) return { fontSize: 0, display: 'none' };
                const logoSize = getLogoSize();
                const gap = showBoth ? 4 : 0;
                const padding = 8;
                const availableWidth = w - padding;
                const availableHeight = showBoth ? h - logoSize - gap - padding : h - padding;
                const widthBasedFontSize = Math.min(16, availableWidth / 6);
                const heightBasedFontSize = Math.min(14, availableHeight / 2);
                const fontSize = Math.max(9, Math.min(widthBasedFontSize, heightBasedFontSize));
                const lineHeight = 1.2;
                const lineHeightPx = fontSize * lineHeight;
                const maxLines = Math.max(1, Math.floor(availableHeight / lineHeightPx));
                const estimatedTextWidth = data.name.length * fontSize * 0.6;
                const needsMultipleLines = estimatedTextWidth > availableWidth;
                const lineClamp = needsMultipleLines ? Math.min(maxLines, 3) : 1;
                return {
                  fontSize,
                  maxHeight: availableHeight,
                  lineHeight,
                  display: 'block',
                  lineClamp,
                  availableWidth
                };
              };

              const [logoSize, textStyles] = [getLogoSize(), getTextStyles()];
              const logoUrl = getLogoUrl(data.website_SAFE_LOGO);
              const isInExcludeAction = showExclusion ? getIsExcludingResult(data.id) : false;
              let backgroundColor = data.color || HEATMAP_COLORS.NEUTRAL;
              backgroundColor = isInExcludeAction
                ? blendWithWhite(backgroundColor)
                : backgroundColor;

              return (
                <Fragment key={uniqueId}>
                  <div
                    id={uniqueId}
                    className={`leaf ${isHovered ? 'hovered' : ''}`}
                    style={{
                      left: x0,
                      top: y0,
                      width: w,
                      height: h,
                      background: backgroundColor,
                      gap: showBoth ? '4px' : '0',
                      padding: showLogo || showText ? '4px' : '0'
                    }}
                    onMouseEnter={() => {
                      // TD-2772 — a tap on a touch device replays as mouseenter here, which
                      // raised the hover card on top of the tile the analyst just opened.
                      if (isTouchRecent()) return;
                      savedState.setHoveredLeaf(data.id);
                      savedState.setHoveredGroup(data[groupProperty]);
                    }}
                    onMouseLeave={() => {
                      savedState.setHoveredLeaf(null);
                      savedState.setHoveredGroup(null);
                    }}
                    onClick={() => {
                      if (!shouldAllowClick()) {
                        state.resetInteraction();
                        return;
                      }
                      state.resetInteraction();
                      if (displayDetails) {
                        displayDetails(data);
                      }
                    }}
                    onTouchEnd={(e) => {
                      // Re-stamp on the way out: `TOUCH_RECENCY_MS` has to be measured from
                      // the END of the gesture, not its start. The long press fires at
                      // 500ms and invites a longer hold, so a stamp taken only at
                      // touchstart expires while the finger is still down — and the mouse
                      // events iOS replays on release then raise the hover card over the
                      // menu that was just opened.
                      lastTouchAtRef.current = Date.now();

                      // TD-2772 — lifting the finger ends the gesture, so the pending
                      // long press must die with it. Without this the timer kept running
                      // and opened the context menu ~400ms after an ordinary tap.
                      clearTilePress(e.currentTarget);
                      handleElementTouchEnd(e, () => {
                        if (displayDetails) {
                          displayDetails(data);
                        }
                      });
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // TD-2772 — on touch this is a duplicate of the long-press timer
                      // below: iOS fires it for the callout gesture and Android Chrome
                      // fires it alongside our timer. Letting it through wrote a second
                      // menu state without `isFromTouch`, which re-enabled the hover
                      // guard in OpenInNewTabContext. The timer owns the touch path.
                      if (isTouchRecent()) return;
                      pubSub.publish({ event: 'icon-button-context-menu-trigger' });
                      if (isInExcludeAction) return;
                      state.setContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                        data,
                        leafId: data.id,
                        group: data[groupProperty]
                      });
                    }}
                    onTouchStart={(e) => {
                      lastTouchAtRef.current = Date.now();

                      if (e.touches.length === 1) {
                        e.stopPropagation(); // Prevent container handlers from interfering

                        const point = getEventPoint(e);
                        // Hold the node, not the event: React pools synthetic events and
                        // nulls `currentTarget` before this timer runs. The previous code
                        // closed over a `let totalMovement` that `onTouchMove` never wrote
                        // (it writes the DOM property), so its guard was always true.
                        const tile = e.currentTarget;

                        tile.longPressTimeout = setTimeout(() => {
                          tile.longPressTimeout = null;
                          if (tile.totalMovement >= TILE_LONG_PRESS_MOVE_TOLERANCE) return;

                          pubSub.publish({ event: 'icon-button-context-menu-trigger' });
                          if (isInExcludeAction) return;
                          state.setContextMenu({
                            x: point.x,
                            y: point.y,
                            data,
                            leafId: data.id,
                            group: data[groupProperty],
                            isFromTouch: true
                          });
                        }, TILE_LONG_PRESS_MS);

                        tile.startPoint = point;
                        tile.totalMovement = 0;
                      }
                    }}
                    onTouchMove={(e) => {
                      const tile = e.currentTarget;
                      if (!tile.longPressTimeout || !tile.startPoint) return;

                      // Keep the window open past the gesture, so the mouse events iOS
                      // replays on release are still recognised as synthetic.
                      lastTouchAtRef.current = Date.now();

                      const point = getEventPoint(e);
                      const deltaX = point.x - tile.startPoint.x;
                      const deltaY = point.y - tile.startPoint.y;

                      // TD-2772 — where the page owns one-finger scrolling (`touch-action:
                      // pan-y`) the full 5px tolerance is too generous: iOS stops
                      // delivering touchmove once it claims the gesture, so a slow scroll
                      // would never reach it and would still end in a context menu. Cancel
                      // on the scroll axis instead — a pan is vertical by definition, a
                      // resting thumb's jitter is not — rather than on any movement at all,
                      // which would leave the long press unreachable on this surface.
                      if (allowPageScroll && Math.abs(deltaY) > TILE_LONG_PRESS_SCROLL_TOLERANCE) {
                        clearTilePress(tile);
                        return;
                      }

                      const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                      tile.totalMovement = movement;

                      if (movement > TILE_LONG_PRESS_MOVE_TOLERANCE) {
                        clearTilePress(tile);
                      }
                    }}
                    onTouchCancel={(e) => {
                      lastTouchAtRef.current = Date.now();
                      clearTilePress(e.currentTarget);
                    }}
                  >
                    {showLogo && (
                      <div className="logo" style={{ width: logoSize, height: logoSize }}>
                        <img
                          src={logoUrl || LOGO_PLACEHOLDER}
                          alt={`Company logo - ${data.name}`}
                          style={{
                            width: logoSize,
                            height: logoSize,
                            borderRadius: logoSize / 2,
                            opacity: isInExcludeAction ? 0.5 : 1
                          }}
                        />
                      </div>
                    )}
                    {showText && (
                      <div
                        className="text-wrap"
                        style={{
                          fontSize: textStyles.fontSize,
                          lineHeight: textStyles.lineHeight,
                          maxHeight: textStyles.maxHeight,
                          maxWidth: textStyles.availableWidth,
                          opacity: isInExcludeAction ? 0.7 : 1
                        }}
                      >
                        <div className="text" style={{ WebkitLineClamp: textStyles.lineClamp }}>
                          {data.name}
                        </div>
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>

          {/* Tooltips using scaled coordinates */}
          {scaledElements.leaves.map((node, i) => {
            const { y0, y1, data } = node;
            const isHovered = savedState.hoveredLeaf === data.id;
            const tooltipConfig = getTooltipConfig(y0, y1);
            const logoUrl = getLogoUrl(data.website_SAFE_LOGO);

            return (
              <Fragment key={`tooltip-${i}-${data.id}`}>
                {isHovered && savedState.showHoveredLeaf && (
                  <div
                    className="profiler-heatmap-search-results-tooltip"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      ...(tooltipConfig.position === 'top-center'
                        ? { top: '27px' }
                        : { bottom: '16px' }),
                      width:
                        state.dimensions.width < 648
                          ? `calc(${state.dimensions.width}px - 48px)`
                          : '600px'
                    }}
                  >
                    <div className="tooltip-content-wrap">
                      <div className="company-name">{data.name}</div>
                      <div className="inner-content-wrap">
                        <div className="left">
                          <div
                            style={{
                              width: '50px',
                              height: '50px',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img
                              src={logoUrl || LOGO_PLACEHOLDER}
                              alt={`Company logo - ${data.name}`}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'contain',
                                borderRadius: '25px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                              }}
                            />
                          </div>
                          <div className="metrics">
                            <div className="pm-row">
                              <div>Sales (€M):</div>
                              <div>{data.revenue || PUNCTUATION.EMPTY_VALUE}</div>
                            </div>
                            {showEbitMargin && (
                              <div className="pm-row">
                                <div>EBIT Margin:</div>
                                <div>{data.ebitPercentage || PUNCTUATION.EMPTY_VALUE}</div>
                              </div>
                            )}
                            {!showCagr4Y && (
                              <div className="pm-row">
                                <div>CAGR 3Y:</div>
                                <div>{data.cagr3Y || PUNCTUATION.EMPTY_VALUE}</div>
                              </div>
                            )}
                            {!!showCagr4Y && (
                              <div className="pm-row">
                                <div>CAGR 4Y:</div>
                                <div>{data.cagr4Y || PUNCTUATION.EMPTY_VALUE}</div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="separator"></div>
                        <div className="right">
                          <ChipsList list={data.industries} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>

        {/* Context menu */}
        {!!state.contextMenu && (
          <OpenInNewTabContext
            contextMenu={state.contextMenu}
            setContextMenu={state.setContextMenu}
            openCompanyInNewTab={openCompanyInNewTab}
            getIsExcludingResult={getIsExcludingResult}
            excludeResult={excludeResult}
          />
        )}
      </div>
    );
  }
);

export default ProfilerHeatmap;
